
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { formatCurrency } from '@/components/borrowings/debt/utils';

const sampleInvestments = [
  {
    id: "inv1",
    type: "Fixed Deposit",
    institution: "HDFC Bank",
    principal: 25000000,
    interestRate: 6.25,
    startDate: "2025-05-01",
    endDate: "2025-08-30",
    interestAccrued: 384000,
    maturityValue: 25384000,
  },
  {
    id: "inv2",
    type: "Fixed Deposit",
    institution: "SBI",
    principal: 30000000,
    interestRate: 6.15,
    startDate: "2025-04-15",
    endDate: "2025-10-15",
    interestAccrued: 512000,
    maturityValue: 30512000,
  },
  {
    id: "inv3",
    type: "Liquid Mutual Fund",
    institution: "SBI Liquid Fund",
    principal: 17500000,
    interestRate: 7.15, // Expected annual return
    startDate: "2025-05-15",
    endDate: null,
    interestAccrued: 223000,
    maturityValue: 17723000,
  },
  {
    id: "inv4",
    type: "Liquid Mutual Fund",
    institution: "HDFC Liquid Fund",
    principal: 15000000,
    interestRate: 7.25, // Expected annual return
    startDate: "2025-04-20",
    endDate: null,
    interestAccrued: 245000,
    maturityValue: 15245000,
  },
];

// Helper function to format dates
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Open-ended';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
}

const InvestmentInventory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [investments] = useState(sampleInvestments);
  
  // Filter investments based on search term
  const filteredInvestments = investments.filter(inv => 
    inv.institution.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.type.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Calculate totals
  const totalInvested = investments.reduce((sum, inv) => sum + inv.principal, 0);
  const totalAccrued = investments.reduce((sum, inv) => sum + inv.interestAccrued, 0);
  const totalMaturity = investments.reduce((sum, inv) => sum + inv.maturityValue, 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Investment Portfolio</h3>
          <p className="text-sm text-muted-foreground">
            {investments.length} active investments
          </p>
        </div>
        <Input 
          placeholder="Search investments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="p-4 border rounded-md bg-muted/30">
          <div className="text-sm text-muted-foreground">Total Invested</div>
          <div className="text-xl font-medium">{formatCurrency(totalInvested)}</div>
        </div>
        <div className="p-4 border rounded-md bg-muted/30">
          <div className="text-sm text-muted-foreground">Interest Accrued</div>
          <div className="text-xl font-medium text-green-600">{formatCurrency(totalAccrued)}</div>
        </div>
        <div className="p-4 border rounded-md bg-muted/30">
          <div className="text-sm text-muted-foreground">Total Value</div>
          <div className="text-xl font-medium">{formatCurrency(totalMaturity)}</div>
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Investment Type</TableHead>
              <TableHead>Institution</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>Maturity</TableHead>
              <TableHead className="text-right">Interest Accrued</TableHead>
              <TableHead className="text-right">Current Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvestments.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>{inv.type}</TableCell>
                <TableCell>{inv.institution}</TableCell>
                <TableCell className="text-right">{formatCurrency(inv.principal)}</TableCell>
                <TableCell className="text-right">{inv.interestRate.toFixed(2)}%</TableCell>
                <TableCell>{formatDate(inv.startDate)}</TableCell>
                <TableCell>{formatDate(inv.endDate)}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(inv.interestAccrued)}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(inv.maturityValue)}</TableCell>
              </TableRow>
            ))}
            
            {filteredInvestments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                  No investments found matching your search criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      <div className="text-sm text-muted-foreground">
        <p>Note: Interest accruals are calculated daily. For mutual funds, returns are based on latest NAV.</p>
      </div>
    </div>
  );
};

export default InvestmentInventory;
